/**
 * Silhouettes des pays, tirées du SVG du monde (Shape Nerd).
 *
 * Le cadrage ne peut pas prendre la boîte englobante du tracé entier : celui
 * de la France contient la Guyane, celui des États-Unis l'Alaska et Hawaï, et
 * la silhouette deviendrait un point au milieu du vide. On découpe donc le
 * tracé en sous-tracés, on retient le plus grand et ceux qui l'entourent de
 * près, et on cadre sur eux ; le reste sort du cadre.
 */

const NUMBER_RE = /[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/gi;
const TOKEN_RE = /[MmLlHhVvCcSsQqTtAaZz]|[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/gi;

/** Nombre d'arguments par commande (les commandes se répètent implicitement). */
const ARITY = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };

/**
 * Boîte englobante de chaque sous-tracé (approchée : points d'ancrage et
 * points de contrôle, largement suffisant pour cadrer).
 * @returns {Array<{minX: number, minY: number, maxX: number, maxY: number}>}
 */
export function subpathBoxes(d) {
	const tokens = String(d).match(TOKEN_RE) || [];
	const boxes = [];
	let box = null;
	let x = 0, y = 0, startX = 0, startY = 0;
	let command = null;
	let i = 0;

	const add = (px, py) => {
		if (!box) box = { minX: px, minY: py, maxX: px, maxY: py };
		else {
			box.minX = Math.min(box.minX, px); box.maxX = Math.max(box.maxX, px);
			box.minY = Math.min(box.minY, py); box.maxY = Math.max(box.maxY, py);
		}
	};
	const close = () => {
		if (box) boxes.push(box);
		box = null;
	};

	while (i < tokens.length) {
		const token = tokens[i];
		if (/^[a-z]$/i.test(token)) {
			command = token;
			i++;
			if (command === "z" || command === "Z") {
				close();
				x = startX; y = startY;
				continue;
			}
		}
		if (!command) { i++; continue; }
		const lower = command.toLowerCase();
		const relative = command === lower;
		const n = ARITY[lower];
		const args = tokens.slice(i, i + n).map(Number);
		if (args.length < n || args.some(Number.isNaN)) break;
		i += n;

		if (lower === "m") {
			close();
			x = relative ? x + args[0] : args[0];
			y = relative ? y + args[1] : args[1];
			startX = x; startY = y;
			add(x, y);
			// Les paires suivantes d'un « m » sont des « l » implicites.
			command = relative ? "l" : "L";
			continue;
		}
		if (lower === "h") { x = relative ? x + args[0] : args[0]; add(x, y); continue; }
		if (lower === "v") { y = relative ? y + args[0] : args[0]; add(x, y); continue; }
		if (lower === "a") {
			x = relative ? x + args[5] : args[5];
			y = relative ? y + args[6] : args[6];
			add(x, y);
			continue;
		}
		// l, c, s, q, t : paires de coordonnées, la dernière est le nouveau point courant.
		for (let k = 0; k < n; k += 2) {
			const px = relative ? x + args[k] : args[k];
			const py = relative ? y + args[k + 1] : args[k + 1];
			add(px, py);
			if (k === n - 2) { x = px; y = py; }
		}
	}
	close();
	return boxes;
}

const area = (b) => (b.maxX - b.minX) * (b.maxY - b.minY);

/**
 * `viewBox` cadré sur l'essentiel d'un pays : le plus grand sous-tracé et ceux
 * dont le centre tombe à moins d'une fois sa taille de ses bords.
 * @param {string} d   attribut `d` du tracé
 * @param {number} [padding] marge relative
 */
export function focusBox(d, padding = 0.08) {
	const boxes = subpathBoxes(d);
	if (!boxes.length) return null;
	const main = boxes.reduce((best, b) => (area(b) > area(best) ? b : best));
	const reach = Math.max(main.maxX - main.minX, main.maxY - main.minY);
	const near = boxes.filter(b => {
		const cx = (b.minX + b.maxX) / 2, cy = (b.minY + b.maxY) / 2;
		return cx >= main.minX - reach && cx <= main.maxX + reach && cy >= main.minY - reach && cy <= main.maxY + reach;
	});
	const u = near.reduce((acc, b) => ({
		minX: Math.min(acc.minX, b.minX), minY: Math.min(acc.minY, b.minY),
		maxX: Math.max(acc.maxX, b.maxX), maxY: Math.max(acc.maxY, b.maxY),
	}), { ...main });
	const w = u.maxX - u.minX, h = u.maxY - u.minY;
	const size = Math.max(w, h);
	const pad = size * padding;
	// Cadre carré centré : toutes les silhouettes occupent la même place à l'écran.
	const cx = u.minX + w / 2, cy = u.minY + h / 2;
	const side = size + 2 * pad;
	const round = (v) => Math.round(v * 1000) / 1000;
	return `${round(cx - side / 2)} ${round(cy - side / 2)} ${round(side)} ${round(side)}`;
}

/** Tracés du SVG du monde présent dans la page : `{ code: d }`. */
export function readWorldPaths(root = document) {
	const paths = {};
	root.querySelectorAll(".shape-source path[id]").forEach(path => {
		const id = path.getAttribute("id");
		if (/^[a-z]{2}$/.test(id)) paths[id] = path.getAttribute("d");
	});
	return paths;
}

export { NUMBER_RE };
